import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

function BlankPhoto() {
  return {
    caption: '',
    kicker: 'Off the clock · 01',
    rotate: 0,
    rotate_img: 0,
    size: 'medium',
    ratio: 'aspect-[4/5]',
    image_data: '',
    order: 0,
    published: true,
  };
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(String(e.target?.result || ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function PhotosAdmin() {
  const [photos, setPhotos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get('/photos/all');
    setPhotos(r.data);
  };

  useEffect(() => { refresh(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 900_000) {
      toast.error('Image too large (max ~900 KB). Compress it first.');
      return;
    }
    const data = await fileToDataURL(file);
    setEditing({ ...editing, image_data: data });
    e.target.value = '';
  };

  const save = async () => {
    if (!editing.caption?.trim()) { toast.error('Caption is required.'); return; }
    if (!editing.image_data && !editing.id) { toast.error('Please pick an image.'); return; }
    setBusy(true);
    try {
      const payload = {
        caption: editing.caption.trim(),
        kicker: editing.kicker || '',
        rotate: Number(editing.rotate) || 0,
        rotate_img: Number(editing.rotate_img) || 0,
        size: editing.size || 'medium',
        ratio: editing.ratio || 'aspect-[4/5]',
        image_data: editing.image_data || '',
        order: Number(editing.order) || 0,
        published: !!editing.published,
      };
      if (editing.id) await api.put(`/photos/${editing.id}`, payload);
      else await api.post('/photos', payload);
      toast.success('Saved');
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    await api.delete(`/photos/${id}`);
    toast.success('Deleted');
    refresh();
  };

  return (
    <section className="mb-16" data-testid="photos-admin">
      <div className="flex items-center justify-between mb-6">
        <h2 className="serif text-3xl text-ink">Off the clock · photos <span className="mono text-sm text-ink/50 ml-3">{photos.length} total</span></h2>
        <button
          type="button"
          onClick={() => setEditing(BlankPhoto())}
          data-testid="new-photo-button"
          className="bg-void text-paper px-5 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors"
        >
          + Add photo
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {photos.map((p) => (
          <div key={p.id} className="relative bg-paper border border-ink/10" data-testid={`admin-photo-${p.id}`}>
            {p.image_data ? (
              <img src={p.image_data} alt={p.caption} className="w-full aspect-square object-cover" />
            ) : (
              <div className="w-full aspect-square bg-lilac/30 flex items-center justify-center mono text-[10px] text-ink/40">no image</div>
            )}
            <div className="p-2 space-y-1">
              <div className="serif text-sm text-ink leading-tight truncate">{p.caption}</div>
              <div className="mono text-[9px] uppercase tracking-[0.2em] text-orchid truncate">{p.kicker}</div>
              <div className="mono text-[9px] text-ink/50">order {p.order} · {p.published ? 'live' : 'draft'}</div>
              <div className="flex gap-1 pt-1">
                <button onClick={() => setEditing(p)} className="flex-1 mono text-[9px] uppercase tracking-[0.15em] px-2 py-1 border border-ink/20 hover:border-orchid">Edit</button>
                <button onClick={() => remove(p.id)} className="flex-1 mono text-[9px] uppercase tracking-[0.15em] px-2 py-1 border border-ink/20 hover:border-red-700">Del</button>
              </div>
            </div>
          </div>
        ))}
        {photos.length === 0 && <div className="col-span-full p-8 mono text-sm text-ink/50 border border-dashed border-ink/20">No photos yet. Click + Add photo to upload the first one.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-void/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto" data-testid="photo-editor">
          <div className="w-full max-w-2xl bg-paper border border-ink/20 p-6 md:p-10">
            <div className="flex items-center justify-between mb-6">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">{editing.id ? 'Edit photo' : 'New photo'}</div>
              <button onClick={() => setEditing(null)} className="mono text-sm">✕</button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* image */}
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-2">Image (jpg / png / webp, ≤ 900 KB)</label>
                <input type="file" accept="image/*" onChange={onFile} data-testid="photo-file" className="w-full mono text-xs" />
                {editing.image_data && (
                  <div className={`mt-3 border border-ink/10 ${editing.ratio || 'aspect-[4/5]'} overflow-hidden`}>
                    <img src={editing.image_data} alt="preview" className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="mt-2 mono text-[10px] text-ink/50">
                  If your image is heavier than 900 KB, drop it into <a href="https://squoosh.app" target="_blank" rel="noreferrer" className="underline">squoosh.app</a> first (free, browser-only).
                </p>
              </div>

              {/* metadata */}
              <div className="space-y-3">
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Caption *</label>
                  <input value={editing.caption} onChange={(e) => setEditing({...editing, caption: e.target.value})} data-testid="photo-caption" placeholder="e.g. Berlin · autumn edit" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-sm py-2" />
                </div>
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Kicker (small label above caption)</label>
                  <input value={editing.kicker} onChange={(e) => setEditing({...editing, kicker: e.target.value})} placeholder="Field trip · 01" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-sm py-2" />
                </div>
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Shape (photo aspect ratio)</label>
                  <select value={editing.ratio || 'aspect-[4/5]'} onChange={(e) => setEditing({...editing, ratio: e.target.value})} data-testid="photo-ratio" className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2">
                    <option value="aspect-[3/4]">Tall (3:4)</option>
                    <option value="aspect-[4/5]">Portrait (4:5)</option>
                    <option value="aspect-square">Square (1:1)</option>
                    <option value="aspect-[4/3]">Landscape (4:3)</option>
                    <option value="aspect-video">Wide (16:9)</option>
                  </select>
                  <p className="mono text-[9px] text-ink/40 mt-1">Controls how the photo is cropped in its polaroid frame.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Size</label>
                    <select value={editing.size} onChange={(e) => setEditing({...editing, size: e.target.value})} className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2">
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large (tall)</option>
                    </select>
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Order</label>
                    <input type="number" value={editing.order} onChange={(e) => setEditing({...editing, order: e.target.value})} className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Tilt (° -8 to 8)</label>
                    <input type="number" value={editing.rotate} onChange={(e) => setEditing({...editing, rotate: e.target.value})} className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2" />
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Image rotation (0, 90, -90)</label>
                    <input type="number" value={editing.rotate_img} onChange={(e) => setEditing({...editing, rotate_img: e.target.value})} className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2" />
                    <p className="mono text-[9px] text-ink/40 mt-1">Use if photo appears sideways (iPhone EXIF lost).</p>
                  </div>
                </div>
                <label className="mono text-xs flex items-center gap-2 pt-2">
                  <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({...editing, published: e.target.checked})} />
                  Published (visible on public site)
                </label>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-8">
              <button onClick={() => setEditing(null)} className="mono text-[11px] uppercase tracking-[0.22em] px-5 py-3 border border-ink/20 hover:border-orchid">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="photo-save" className="bg-void text-paper px-6 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
